import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { knowledgeApi } from "../../../api/knowledgeApi";
import { DocumentViewer } from "../../../components/DocumentViewer";
import { SupabaseKnowledgeDocument } from "../../../types/supabase";

const categoryColorMap = {
  flashcards: "bg-blue-200",
  articles: "bg-green-200",
  videos: "bg-red-200",
};

const KnowledgeManager = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [documents, setDocuments] = useState<SupabaseKnowledgeDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] =
    useState<SupabaseKnowledgeDocument | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const navigate = useNavigate();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] =
    useState<SupabaseKnowledgeDocument | null>(null);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const docs = await knowledgeApi.getDocuments();
      setDocuments(docs);
    } catch (error) {
      console.error("Failed to load documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDocuments = documents.filter((doc) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      doc.name.toLowerCase().includes(searchLower) ||
      doc.description.toLowerCase().includes(searchLower) ||
      doc.category.toLowerCase().includes(searchLower)
    );
  });

  const handleViewDocument = (document: SupabaseKnowledgeDocument) => {
    setSelectedDocument(document);
    setIsViewerOpen(true);
  };

  const handleDeleteDocument = (document: SupabaseKnowledgeDocument) => {
    setDocumentToDelete(document);
    setDeleteModalOpen(true);
  };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Knowledge Documents</h1>
        <div className="flex gap-3">
          <button
            className="bg-amber-400 text-black px-4 py-2 rounded-md hover:bg-amber-600 transition-colors"
            onClick={() => {
              navigate("/coordinator/knowledge-manager/rag-test");
            }}
          >
            Test Retrieval
          </button>
          <button
            className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition-colors"
            onClick={() => {
              navigate("/coordinator/knowledge-manager/upload");
            }}
          >
            Add Document
          </button>
        </div>
      </div>

      <div className="mb-6 rounded-xl border bg-white shadow-sm">
        <div className="border-b p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border py-2 pl-10 pr-4 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#ffcc32]"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
            <p className="text-gray-600">Loading documents...</p>
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-600">No documents found</p>
          </div>
        ) : (
          <div className="space-y-4 p-6">
            {filteredDocuments.map((doc) => (
              <div
                key={doc.uuid}
                className="rounded-xl border bg-white shadow-sm p-5 transition-colors hover:bg-gray-50"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="mb-2 flex items-center gap-3">
                      <span
                        className={`text-xs rounded-full px-3 py-1 font-medium text-gray-700 ${categoryColorMap[doc.category as keyof typeof categoryColorMap]}`}
                      >
                        {doc.category.charAt(0).toUpperCase() +
                          doc.category.slice(1)}
                      </span>
                    </div>
                    <h2 className="mb-2 text-lg font-semibold text-gray-900">
                      {doc.name}
                    </h2>
                    <p className="text-sm text-gray-600">{doc.description}</p>
                  </div>
                  <button
                    onClick={() => handleViewDocument(doc)}
                    className="ml-4 text-sm font-medium text-[#4CAF50] hover:underline whitespace-nowrap"
                  >
                    View Document
                  </button>
                  <button
                    onClick={() => handleDeleteDocument(doc)}
                    className="ml-4 text-sm font-medium text-red-400 hover:underline whitespace-nowrap"
                  >
                    Delete Document
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Document Viewer Modal */}
      {selectedDocument && (
        <DocumentViewer
          document={selectedDocument}
          isOpen={isViewerOpen}
          onClose={() => {
            setIsViewerOpen(false);
            setSelectedDocument(null);
          }}
        />
      )}

      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-8 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">Delete Document</h2>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete{" "}
              <span className="font-bold">{documentToDelete?.name}</span>?
            </p>
            <div className="flex justify-end gap-4">
              <button
                className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition-colors"
                onClick={() => {
                  if (documentToDelete) {
                    knowledgeApi.deleteDocumentFromStorage(
                      documentToDelete.name
                    );
                    knowledgeApi.delete(documentToDelete.uuid); // Deletes record from database
                    loadDocuments();
                  }
                  setDeleteModalOpen(false);
                }}
              >
                Delete
              </button>
              <button
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
                onClick={() => setDeleteModalOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeManager;
